import React from "react";
import { Box, Divider, IconButton, Text } from "@origin-digital/ods-core";
import styled from "@emotion/styled";
import { IconDelete, IconError } from "@origin-digital/ods-icons";
import { BulkDuplicateTextField } from "./BulkDuplicateTextField";
import { useFormikContext } from "formik";
import { formatAddressFromBulkSelection } from "../helpers";
import { BulkDuplicateFormValues, PremiseWithError } from "./types";
import { useResponsiveBelowMd } from "src/hooks";
//import { validateBulkDuplicatePremisesPerRow } from "src/components/Form/FormComponents/Validations";

const RowNumberBox = styled(Box)`
  min-width: 44px;
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface BulkDuplicatePremisesRowProps {
  premise: PremiseWithError;
  index: number;
  handleBulkDuplicatePremisesValidation: (
    index: number,
    premises?: PremiseWithError[]
  ) => void;
  isAddingPremise: boolean;
}

const BulkDuplicatePremisesRow: React.FC<BulkDuplicatePremisesRowProps> = ({
  premise,
  index,
  handleBulkDuplicatePremisesValidation,
  isAddingPremise,
}) => {
  const isBelowMd = useResponsiveBelowMd();
  const { values, setFieldValue, /* setErrors, */ isSubmitting } =
    useFormikContext<BulkDuplicateFormValues>();
  const { selectedAddressParts } = values;
  const rowRef = React.useRef<HTMLDivElement>(null);
  const buttonClickedRef = React.useRef(false);

  React.useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isAddButton =
        target.getAttribute("data-id") === "add-premises-button" ||
        target.closest('[data-id="add-premises-button"]') !== null;

      if (isAddButton) {
        buttonClickedRef.current = true;
      }
    };

    const handleClick = () => {
      setTimeout(() => {
        buttonClickedRef.current = false;
      }, 150);
    };

    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("click", handleClick, true);
    };
  }, []);

  const handleDeletePremise = () => {
    const updatedPremises = values.premises.filter((_, i) => i !== index);
    setFieldValue("premises", updatedPremises);
  };

  const handleRowBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;

    // Skip validation if add premise button is being clicked
    if (isAddingPremise) {
      return;
    }

    // Return false (skip validation) if button was clicked
    if (buttonClickedRef.current || isAddingPremise) {
      return;
    }

    // Check if the focus is moving to another element within the same row
    if (
      rowRef.current &&
      relatedTarget &&
      rowRef.current.contains(relatedTarget)
    ) {
      return;
    }
    handleBulkDuplicatePremisesValidation(index);
  };

  const hasError = premise.hasError;
  const addressPartsKeys = Object.keys(values.addressParts);
  const fieldErrors = premise.fieldErrors || {};
  const errors = premise.errors || [];
  return (
    <div ref={rowRef} onBlur={(e) => handleRowBlur(e)}>
      <Box
        key={index}
        display="flex"
        flexDirection="row"
        alignItems={{ xs: "flex-start", lg: "center" }}
        paddingY="small"
        backgroundColor={hasError ? undefined : "grey50"}
        style={hasError ? { background: "rgba(221, 0, 0, 0.08)" } : undefined}
      >
        {/* Row no. */}
        <RowNumberBox paddingY="medium">
          <Text tone="disabledText" align="center">
            {index + 1}
          </Text>
        </RowNumberBox>

        {/* Fields container - wraps on XS (single column), SM-MD (2 columns), horizontal on LG+ */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row", lg: "row" }}
          flexWrap={{ sm: "wrap", lg: "nowrap" }}
          style={{ flex: 1 }}
        >
          {/* DP no. field */}
          {addressPartsKeys.includes("dp_number") && (
            <BulkDuplicateTextField
              id={`dp_number_${index}`}
              label={isBelowMd ? "DP no." : ""}
              fieldLabel="DP"
              value={premise.dp_number || ""}
              showComma={true}
              readOnly={
                !selectedAddressParts.includes("dp_number") || isSubmitting
              }
              premiseIndex={index}
              fieldName="dp_number"
              hasError={!!fieldErrors.dp_number}
            />
          )}
          {/* End DP no. field */}

          {/* Unit no. field */}
          {addressPartsKeys.includes("unit_number") && (
            <BulkDuplicateTextField
              id={`unit_number_${index}`}
              label={isBelowMd ? "Unit no." : ""}
              fieldLabel="Unit"
              value={premise.unit_number || ""}
              showComma={true}
              readOnly={
                !selectedAddressParts.includes("unit_number") || isSubmitting
              }
              premiseIndex={index}
              fieldName="unit_number"
              hasError={!!fieldErrors.unit_number}
            />
          )}
          {/* End unit no. field */}

          {/* Block no. field */}
          {addressPartsKeys.includes("block_number") && (
            <BulkDuplicateTextField
              id={`block_number_${index}`}
              label={isBelowMd ? "Block no." : ""}
              fieldLabel="Block"
              value={premise.block_number || ""}
              showComma={true}
              readOnly={
                !selectedAddressParts.includes("block_number") || isSubmitting
              }
              premiseIndex={index}
              fieldName="block_number"
            />
          )}
          {/* End Block no. field */}

          {/* Level no. field */}
          {addressPartsKeys.includes("floor_number") && (
            <BulkDuplicateTextField
              id={`floor_number_${index}`}
              label={isBelowMd ? "Level no." : ""}
              fieldLabel="Level"
              value={premise.floor_number || ""}
              showComma={true}
              readOnly={
                !selectedAddressParts.includes("floor_number") || isSubmitting
              }
              premiseIndex={index}
              fieldName="floor_number"
              hasError={!!fieldErrors.floor_number}
            />
          )}
          {/* End Level no. field */}

          {/* Section no. field */}
          {addressPartsKeys.includes("section_number") && (
            <BulkDuplicateTextField
              id={`section_number_${index}`}
              label={isBelowMd ? "Section no." : ""}
              fieldLabel="Section"
              value={premise.section_number || ""}
              showComma={true}
              readOnly={
                !selectedAddressParts.includes("section_number") || isSubmitting
              }
              premiseIndex={index}
              fieldName="section_number"
              hasError={!!fieldErrors.section_number}
            />
          )}
          {/* End section no. field */}

          {/* Lot no. field */}
          {addressPartsKeys.includes("lot_number") && (
            <BulkDuplicateTextField
              id={`lot_number_${index}`}
              label={isBelowMd ? "Lot no." : ""}
              fieldLabel="Lot"
              value={premise.lot_number || ""}
              showComma={true}
              readOnly={
                !selectedAddressParts.includes("lot_number") || isSubmitting
              }
              premiseIndex={index}
              fieldName="lot_number"
              hasError={!!fieldErrors.lot_number}
            />
          )}
          {/* End lot no. field */}

          {/* Street no. field */}
          {addressPartsKeys.includes("street_number") && (
            <BulkDuplicateTextField
              id={`street_number_${index}`}
              label={isBelowMd ? "Street no." : ""}
              value={premise.street_number || ""}
              showComma={false}
              readOnly={
                !selectedAddressParts.includes("street_number") || isSubmitting
              }
              premiseIndex={index}
              fieldName="street_number"
              hasError={!!fieldErrors.street_number}
            />
          )}
          {/* End street no. field */}

          {/* Address text */}
          <Box
            paddingX="xsmall"
            paddingY={{ xs: "small", lg: "none" }}
            display="flex"
            alignItems="center"
            className="field-item field-item-address"
          >
            {/* <Text>Exhibition St, Melbourne VIC 3000</Text> */}
            <Text>{formatAddressFromBulkSelection(values.addressObj)}</Text>
          </Box>

          {/* End address text */}
        </Box>
        {/* End fields container */}
        <Box
          paddingX="xsmall"
          paddingY={{ xs: "small", lg: "none" }}
          display="flex"
          alignItems="center"
        >
          {/* {hasError && <Text>Oops, please fill in all required fields.</Text>} */}
        </Box>

        {/* Delete button */}
        <Box
          paddingX="xsmall"
          paddingY={{ xs: "xsmall", lg: "none" }}
          display="flex"
          alignItems="center"
        >
          <IconButton
            aria-label="Delete premise"
            variant="transparent"
            onClick={handleDeletePremise}
          >
            <IconDelete size="xsmall" />
          </IconButton>
        </Box>
        {/* End delete button */}
      </Box>
      {/* Validation error message */}
      {hasError && (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          paddingLeft="xlarge"
          style={{ background: "rgba(221, 0, 0, 0.08)" }}
        >
          {/* Note for Dev: could potentially use <FormHelperText> component here? */}
          <Box
            display="flex"
            alignItems={{ xs: "flex-start" }}
            gap="medium"
            flexDirection="column"
            paddingLeft="medium"
            paddingTop="none"
            paddingBottom="small"
          >
            {errors.map((error, errIndex) => (
              <Box
                key={errIndex}
                display="flex"
                alignItems="center"
                gap="xxsmall"
              >
                <IconError color="critical" size="xsmall" variant="solid" />
                <Text variant="body-small" tone="critical">
                  {error}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {/* End validation error message */}
      <Divider />
    </div>
  );
};
export default BulkDuplicatePremisesRow;
